import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";

import { fieldDescribedBy } from "ui/forms/field-message/field-message.helpers";
import { InputControl } from "ui/forms/input-control/input-control";
import { useFieldId } from "ui/forms/use-field-id";
import { CALENDAR_DEFAULTS } from "ui/date-picker/date-picker.constants";
import { type DatePickerProps } from "ui/date-picker/date-picker.types";
import { CLEAR_BUTTON_SX, OPEN_BUTTON_SX } from "ui/date-picker/date-picker.styles";


import Icons from "assets";

/**
 * Components of their own, not `Icons.Event` / `Icons.QuickTipClose` directly: MUI X hands an icon slot an
 * `ownerState`, which would otherwise reach the `<svg>` as an unknown DOM attribute. Their size is the
 * field geometry's.
 */
const CalendarIcon = () => <Icons.Event aria-hidden />;

const ClearIcon = () => <Icons.QuickTipClose aria-hidden />;

export const DatePicker = ({
  ref,
  id,
  name,
  label,
  required,
  disabled,
  errMessage,
  helperText,
  infoTooltip,
  size = "medium",
  onBlur,
  onClear,
  "data-testid": dataTestId,
  sx,
  slots,
  slotProps,
  ...rest
}: DatePickerProps) => {
  const fieldId = useFieldId(id, name);

  return (
    <InputControl
      id={fieldId}
      label={label}
      infoTooltip={infoTooltip}
      required={required}
      disabled={disabled}
      errorMessage={errMessage}
      helperText={helperText}
      size={size}
      sx={sx}>
      <DesktopDatePicker
        {...CALENDAR_DEFAULTS}
        {...rest}
        inputRef={ref}
        name={name}
        disabled={disabled}
        slots={{ ...slots, openPickerIcon: CalendarIcon, clearIcon: ClearIcon }}
        slotProps={{
          ...slotProps,
          textField: {
            id: fieldId,
            fullWidth: true,
            required,
            error: errMessage ? true : undefined,
            onBlur,
            // Through to MUI X, which is what selects the field geometry's size.
            size,
            slotProps: {
              input: {
                "aria-labelledby": label ? `${fieldId}-label` : undefined,
                "aria-describedby": fieldDescribedBy(fieldId, {
                  errorMessage: errMessage,
                  helperText,
                }),
                "data-testid": dataTestId ?? `${name ?? fieldId}-date-input`,
              },
            },
          },
          // MUI X shows the button only while there is a value to clear and the field is editable.
          field: { clearable: !!onClear, onClear },
          openPickerButton: { sx: OPEN_BUTTON_SX },
          clearButton: { sx: CLEAR_BUTTON_SX },
        }}
      />
    </InputControl>
  );
};
